/**
 * Sessions Service
 *
 * Client-side Firestore queries for session history.
 * Lists, loads, and manages officer sessions.
 */

import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { initFirebaseClient } from '@copapp/firebase/client';
import { COLLECTIONS, SUBCOLLECTIONS } from '@copapp/core/constants';

const getDb = () => initFirebaseClient().db;

export interface SessionListItem {
  sessionId: string;
  status: 'active' | 'paused' | 'completed' | 'travesty';
  category: string;
  difficulty: string;
  scenarioTitle: string;
  scenarioPreview: string;
  turnCount: number;
  deescalationStatus: string;
  createdAt: string;
  updatedAt: string;
  /** Certified score from the clean report, when one exists. */
  rating: number | null;
}

/**
 * List officer's sessions, most recent first.
 */
export async function listSessions(
  officerUid: string,
  maxResults = 20,
): Promise<SessionListItem[]> {
  const db = getDb();
  const sessionsRef = collection(
    db,
    COLLECTIONS.OFFICERS,
    officerUid,
    SUBCOLLECTIONS.SESSIONS,
  );

  const q = query(sessionsRef, orderBy('createdAt', 'desc'), limit(maxResults));
  const snap = await getDocs(q);

  return snap.docs.map((d) => {
    const data = d.data();
    const lastTurn = data.turnHistory?.length > 0
      ? data.turnHistory[data.turnHistory.length - 1]
      : null;

    return {
      sessionId: d.id,
      status: data.status || 'active',
      category: data.category || 'general',
      difficulty: data.difficulty || 'moderate',
      scenarioTitle: data.scenario?.title || data.category || 'Simulation',
      scenarioPreview: (data.scenario?.content || data.scenario?.description || '').slice(0, 120) + '...',
      turnCount: data.metrics?.currentTurn || 0,
      deescalationStatus: lastTurn?.deescalationStatus || 'N/A',
      createdAt: data.createdAt || '',
      updatedAt: data.updatedAt || '',
      rating: typeof data.reports?.clean?.rating === 'number' ? data.reports.clean.rating : null,
    };
  });
}

/**
 * Load a full session for resuming or viewing.
 */
export async function loadSession(
  officerUid: string,
  sessionId: string,
): Promise<Record<string, unknown> | null> {
  const db = getDb();
  const sessionRef = doc(
    db,
    COLLECTIONS.OFFICERS,
    officerUid,
    SUBCOLLECTIONS.SESSIONS,
    sessionId,
  );

  const snap = await getDoc(sessionRef);
  if (!snap.exists()) return null;
  return { sessionId: snap.id, ...snap.data() };
}

/**
 * Pause a session (set status to paused).
 */
export async function pauseSession(
  officerUid: string,
  sessionId: string,
): Promise<void> {
  const db = getDb();
  const sessionRef = doc(
    db,
    COLLECTIONS.OFFICERS,
    officerUid,
    SUBCOLLECTIONS.SESSIONS,
    sessionId,
  );

  await updateDoc(sessionRef, {
    status: 'paused',
    updatedAt: new Date().toISOString(),
  });
}
